import React from 'react'
import SelectInput from "components/inputs/selectInput"
import { useTranslation } from "react-i18next"
import { Category, StockStatus } from 'types'
import TextInput from 'components/inputs/textInput'
import Collapse from 'components/collapse/Collapse'
import { ReactComponent as FilterIcon } from 'assets/icons/filter.svg'
import { SortDirection } from 'types'
import { ProductsSortBy, ProductDraftFilter } from 'hooks/useProducts'
import ToggleInput from '@/components/inputs/toggleInput'
import InputGroup from '@/components/inputs/InputGroup'

interface ProductFilterProps {
    filterStockStatus: StockStatus,
    setFilterStockStatus: (str: StockStatus) => void
    filterDraftStatus: ProductDraftFilter,
    setFilterDraftStatus: (str: ProductDraftFilter) => void
    filterQuery: string,
    setFilterQuery: (str: string) => void,
    filterCategory: string,
    setFilterCategory?: (str: string) => void,
    categories: Category[],
    isLoading?: boolean
    allowedSortBy: string[],
    sortBy: string,
    setSortBy: (str: ProductsSortBy) => void,
    sortDirection: SortDirection,
    setSortDirection: (str: SortDirection) => void,
    displayDeleted: boolean,
    setDisplayDeleted: (val: boolean) => void
}

const ProductFilter: React.FC<ProductFilterProps> = ({
    filterStockStatus,
    setFilterStockStatus,
    filterDraftStatus,
    setFilterDraftStatus,
    filterQuery,
    setFilterQuery,
    filterCategory,
    setFilterCategory,
    categories,
    isLoading,
    allowedSortBy,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    displayDeleted,
    setDisplayDeleted
}) => {

    const { t } = useTranslation()

    const handleFilterStockStatusChange = (str: string) => {
        setFilterStockStatus(str as StockStatus)
    }

    const handleFilterDraftStatusChange = (str: string) => {
        setFilterDraftStatus(str as ProductDraftFilter)
    }

    const handleFilterQueryChange = (str: string) => {
        setFilterQuery(str)
    }

    const printCategoryHierarchy = (categoryId: string, categoryName: string, level: number): React.JSX.Element | null => {
        return (
            <React.Fragment key={`category-parent-${categoryId}`}>
                <option key={categoryId} value={categoryId}>{Array(level).fill('—').join('')}{level ? ' ' : ''}{categoryName}</option>
                {categories.map((childCategoryData, _key) => {
                    if (childCategoryData.relations?.parentCategory === categoryId && childCategoryData.id) {
                        return printCategoryHierarchy(childCategoryData.id, childCategoryData.name, level + 1)
                    }
                    return null
                })}
            </React.Fragment>
        )
    }

    return (
        <div className="flex flex-col gap-y-4">
            <Collapse textOpen={t(`labelHideFilters`)} textClosed={t(`labelShowFilters`)} icon={<FilterIcon />} hideToggleArrow={true}>
                <TextInput ignore={true} label={t('labelFilterQuery')} value={filterQuery} onChange={(e) => handleFilterQueryChange(e.target.value)} disabled={isLoading} />
                <InputGroup forcedColumns={true}>
                    <SelectInput ignore={true} label={t('labelFilterStockStatus')} value={filterStockStatus} onChange={(e) => handleFilterStockStatusChange(e.target.value)} disabled={isLoading}>
                        <option value="all">{t('all')}</option>
                        <option value="inStock">{t('inStock')}</option>
                        <option value="lowStock">{t('lowStock')}</option>
                        <option value="outOfStock">{t('outOfStock')}</option>
                        <option value="negativeStock">{t('negativeStock')}</option>
                    </SelectInput>
                    <SelectInput ignore={true} label={t('labelFilterDraftStatus')} value={filterDraftStatus} onChange={(e) => handleFilterDraftStatusChange(e.target.value)} disabled={isLoading}>
                        <option value="all">{t('all')}</option>
                        <option value="publishedOnly">{t('publishedOnly')}</option>
                        <option value="draftsOnly">{t('draftsOnly')}</option>
                    </SelectInput>
                    <SelectInput ignore={true} label={t('labelFilterCategory')} value={filterCategory} onChange={(e) => setFilterCategory && setFilterCategory(e.target.value)} disabled={!setFilterCategory || isLoading}>
                        <option value="">{t('all')}</option>
                        {categories.map((categoryData, _key) => {
                            if (categoryData.relations?.parentCategory || !categoryData.id) return null
                            return printCategoryHierarchy(categoryData.id, categoryData.name, 0)
                        })}
                    </SelectInput>
                    {/* Sort By Select */}
                    <SelectInput ignore={true} label={t('labelSortBy')} value={sortBy} onChange={e => setSortBy(e.target.value as ProductsSortBy)} disabled={isLoading}>
                        {allowedSortBy.map((field) => (
                            <option key={field} value={field}>{t(field)}</option>
                        ))}
                    </SelectInput>
                    {/* Sort Direction Select */}
                    <SelectInput ignore={true} label={t('labelSortDirection')} value={sortDirection} onChange={e => setSortDirection(e.target.value as SortDirection)} disabled={isLoading}>
                        <option value="asc">{t('labelAscending')}</option>
                        <option value="desc">{t('labelDescending')}</option>
                    </SelectInput>
                </InputGroup>
                <ToggleInput label={t('labelDisplayDeleted')} checked={displayDeleted} onChange={e => setDisplayDeleted(e.target.checked)} disabled={isLoading} />
            </Collapse>
        </div>
    )
}

export default ProductFilter
